import { gql } from "@apollo/client";

// for one year and three year page
export const USER_FIELDS = gql`
  fragment UserFields on User {
    id
    profilePicture
    name {
      first
      last
    }
  }
`;

export const SUCCESS_CRITERIA_FIELDS = gql`
  fragment SuccessCriteriaFields on SuccessCriteria {
    id
    value
    done
    kanbanStatus
    targetDate
  }
`;

export const ROCK_FIELDS = gql`
  ${SUCCESS_CRITERIA_FIELDS}
  ${USER_FIELDS}
  fragment RockFields on Rock {
    id
    value
    index
    status
    kanbanStatus
    orgNumber
    hide
    number
    successCriterias {
      ...SuccessCriteriaFields
    }
    users {
      ...UserFields
    }
    plan {
      id
      departmentName
      color
      shortName
      sharedPlanId
      plan {
        id
        year
      }
    }
    notes {
      id
    }
    todos {
      id: _id
      createdAt
    }
  }
`;

export const OBJECTIVE_FIELDS = gql`
  ${USER_FIELDS}
  ${ROCK_FIELDS}
  fragment ObjectiveFields on Objective {
    id
    value
    description
    number
    status
    statusHistory {
      period
      status
    }
    category
    users {
      ...UserFields
    }
    rocks {
      ...RockFields
    }
    objectives
    metrics
    todos {
      id: _id
      createdAt
    }
    notes {
      id
    }
    plan {
      id
      color
      shortName
      departmentName
      sharedPlanId
      periodData {
        periodNumber
        reviewed
        periodGrade {
          topHit
          topMiss
          grade
        }
      }
    }
  }
`;

export const METRIC_FIELDS = gql`
  ${USER_FIELDS}
  fragment MetricFields on Metric {
    id
    value
    number
    comparator
    comparisonInfo {
      target
      targetMin
      targetMax
      comparand
    }
    category
    measurables {
      value
      notes {
        id
      }
      reference {
        id
        calculateTotal
        measurables {
          value
        }
      }
      distribution
    }
    unit
    cumulative
    calculateTotal
    enableFormula
    formula
    formulaScope {
      varType
      value
    }
    weeklyTarget
    threeYearMetric
    users {
      ...UserFields
    }
    status
    todos {
      id: _id
      createdAt
    }
    objectives
    notes {
      id
    }
    plan {
      id
      color
      shortName
      departmentName
      sharedPlanId
      year
      plan {
        id
      }
      periodData {
        periodNumber
        reviewed
        periodGrade {
          topHit
          topMiss
          grade
        }
      }
    }
  }
`;

export const WEEKLY_TARGET_FIELDS = gql`
  ${USER_FIELDS}
  fragment WeeklyTargetFields on WeeklyTarget {
    id: _id
    value
    target
    targetMin
    targetMax
    enableFormula
    formula
    formulaScope {
      varType
      value
    }
    unit
    accuracy
    number
    measurables {
      week
      value
      notes {
        id
      }
    }
    plans {
      id
      departmentName
      closed
      shortName
      color
      sharedPlanId
      year
      plan {
        id
        year
      }
    }
    user {
      ...UserFields
    }
    notes {
      id
    }
    todos {
      id: _id
      createdAt
    }
    hide
    oneYearMetric
  }
`;

export const TODO_FIELDS = gql`
  ${USER_FIELDS}
  fragment TodoFields on Todo {
    id: _id
    value
    description
    done
    priority
    referenceId
    referenceModel
    kanbanStatus
    orgNumber
    createdAt
    category
    plan {
      id
      departmentName
      shortName
      color
      year
      plan {
        id
        year
      }
    }
    user {
      ...UserFields
    }
    notes {
      id
    }
    todos {
      id: _id
      createdAt
    }
  }
`;

export const CHART_FIELDS = gql`
  fragment ChartFields on Chart {
    id: _id
    isCompanyChart
    isFavorite
    createdBy
    updatedBy
    createdAt
    updatedAt
    name
    status
    organization
    plan {
      id
      departmentName
      color
      shortName
    }
    cards {
      cardId
      parentNode
      childNodes
      assistantNodes
      user {
        id
        name {
          first
          last
        }
      }
      name
      title
      roles
      responsibilities
    }
  }
`;

export const MEETING_FIELDS = gql`
  fragment MeetingFields on Meeting {
    id
    title
    plan {
      id
      departmentName
      color
      shortName
    }
    status
    startTime
    timezone
    frequency
    type
    owner {
      id
      name {
        first
        last
      }
      profilePicture
    }
    users {
      id
      name {
        first
        last
      }
      profilePicture
    }
    steps {
      name
      value
      duration
    }
    endTime
    rating
  }
`;

export const KANBAN_ITEM_FIELDS = gql`
  ${SUCCESS_CRITERIA_FIELDS}
  ${USER_FIELDS}
  fragment KanbanItemFields on KanbanItem {
    ... on Todo {
      id: _id
      value
      description
      priority
      category
      orgNumber
      done
      kanbanStatus
      kanbanPosition
      referenceId
      referenceModel
      user {
        ...UserFields
      }
      plan {
        id
        shortName
        departmentName
        color
        plan {
          id
        }
      }
      referenceDoc {
        ... on Todo {
          value
        }
        ... on Metric {
          value
        }
        ... on Objective {
          value
        }
        ... on Rock {
          value
        }
        ... on SuccessCriteria {
          value
        }
        ... on WeeklyTarget {
          value
        }
      }
    }
    ... on Rock {
      id
      value
      orgNumber
      index
      status
      kanbanStatus
      kanbanPosition
      objective {
        id
        value
      }
      users {
        ...UserFields
      }
      plan {
        id
        shortName
        departmentName
        color
        plan {
          id
        }
      }
      successCriterias {
        ...SuccessCriteriaFields
      }
    }
    ... on SuccessCriteria {
      ...SuccessCriteriaFields
      kanbanPosition
      orderIdx
      rock {
        id
        value
        orgNumber
        plan {
          id
          shortName
          departmentName
          color
          plan {
            id
          }
        }
      }
      users {
        ...UserFields
      }
    }
  }
`;
